import React from "react"
import { Formik, FormikHelpers } from "formik"
import * as yup from "yup"
import {
    Box, Stack, Grid, TextInput, Textarea, Title, Text,
    Group, Button, NativeSelect, Loader, Select, MultiSelect,
    Card,
    Avatar,
    Paper,
    Badge
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { notifications } from "@mantine/notifications"
import { Icon } from "@iconify/react/dist/iconify.js"

import { useAppState, useActions } from "src/state"
import { UserFragment } from "src/state/gql/_types"
import { Icons } from "src/components/ui/Icons"
import { usTimezones } from "src/utils/dates"
import { changeEmptyPropsDeep } from "src/utils/objects"
import { statesList } from "src/utils/forms"

type ProfileFormValues = {
    first_name: string
    last_name: string
    alias: string
    phone: string
    bday: string
    timezone: string
    color: string
    address_line1: string
    address_line2: string
    city: string
    state: string
    postal_code: string
    country: string
    website: string
    instagram: string
    facebook: string
    x: string
    tiktok: string
    genres: string
    bio: string
}

const validationSchema = yup.object().shape({
    first_name: yup.string().nullable(),
    last_name: yup.string().nullable(),
    alias: yup.string().nullable(),
    phone: yup.string().nullable(),
    bday: yup.string().nullable(),
    timezone: yup.string().nullable(),
    color: yup.string().nullable(),
    address_line1: yup.string().nullable(),
    address_line2: yup.string().nullable(),
    city: yup.string().nullable(),
    state: yup.string().nullable(),
    postal_code: yup.string().nullable(),
    country: yup.string().nullable(),
    website: yup.string().nullable(),
    instagram: yup.string().nullable(),
    facebook: yup.string().nullable(),
    x: yup.string().nullable(),
    tiktok: yup.string().nullable(),
    genres: yup.string().nullable(),
    bio: yup.string().nullable(),
})

const extractFormValues = (profile: UserFragment): ProfileFormValues => ({
    first_name: profile.first_name || "",
    last_name: profile.last_name || "",
    alias: profile.alias || "",
    phone: profile.phone || "",
    bday: profile.bday || "",
    timezone: profile.timezone || "",
    color: profile.color || "",
    address_line1: profile.address_line1 || "",
    address_line2: profile.address_line2 || "",
    city: profile.city || "",
    state: profile.state || "",
    postal_code: profile.postal_code || "",
    country: profile.country || "",
    website: profile.website || "",
    instagram: profile.instagram || "",
    facebook: profile.facebook || "",
    x: profile.x || "",
    tiktok: profile.tiktok || "",
    genres: profile.genres || "",
    bio: profile.bio || "",
})

const parseDateValue = (value: string) => {
    let isSet = typeof value === "string" && !!value.length
    if (!isSet) return null
    let rawDate = new Date(Date.parse(value))
    rawDate.setMinutes(rawDate.getMinutes() + rawDate.getTimezoneOffset())
    return typeof value === "object" ? value : rawDate
}

const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 10)
    if (digits.length === 0) return ""
    if (digits.length <= 3) return `(${digits}`
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const unformatPhone = (value: string): string => value.replace(/\D/g, "").slice(0, 10)

const timezoneOptions = [...usTimezones]

export const EditableTalentProfile = () => {

    const profile = useAppState(state => state.profile)
    const { updateProfile } = useActions()

    if (!profile) return <Loader />

    const initialValues = extractFormValues(profile)

    const onSubmit = async (
        values: ProfileFormValues,
        { setSubmitting, resetForm }: FormikHelpers<ProfileFormValues>
    ) => {
        const payload = changeEmptyPropsDeep(values)
        const success = await updateProfile(payload)

        if (success) {
            notifications.show({
                icon: <Icon icon={Icons.warningCircle} width={24} />,
                color: "green",
                autoClose: 4000,
                title: "Success!",
                message: "Profile Updated",
            })
            resetForm({ values })
        } else {
            notifications.show({
                icon: <Icon icon={Icons.warningCircle} width={24} />,
                color: "red",
                autoClose: 4000,
                title: "Oops!",
                message: "Could Not Update Profile",
            })
        }
        setSubmitting(false)
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnMount={false}
            validateOnChange={true}
            enableReinitialize={true}
        >
            {({ values, errors, handleBlur, handleSubmit, submitForm, isSubmitting, isValid, dirty, setFieldValue, resetForm }) => (
                <Box component="form" onSubmit={handleSubmit}>
                    <Grid gutter="lg">

                        <Grid.Col span={{ base: 12 }}>
                            <Card padding="lg" radius="md">
                                <Group justify="space-between" align="flex-start" wrap="wrap">
                                    <Group align="center" gap="md">
                                        <Avatar
                                            name={`${values.first_name} ${values.last_name}`}
                                            size={64}
                                            radius="xl"
                                            color="initials"
                                        />
                                        <Stack gap={2}>
                                            <Group gap="xs" align="center">
                                                <Title order={3}>
                                                    {`${values.first_name || ""} ${values.last_name || ""}`.trim() || "Unnamed Talent"}
                                                </Title>
                                                {values.alias && (
                                                    <Badge variant="light" color="violet">{values.alias}</Badge>
                                                )}
                                            </Group>
                                            <Text size="sm" c="dimmed">
                                                {profile.email || "No email on file"}
                                            </Text>
                                        </Stack>
                                    </Group>
                                    <Group gap="xs" mt={{ base: "xs", sm: 0 }}>
                                        {(values.city || values.state) && (
                                            <Badge variant="light" leftSection={<Icon icon={Icons.location} width={14} />}>
                                                {[values.city, values.state].filter(Boolean).join(", ")}
                                            </Badge>
                                        )}
                                    </Group>
                                </Group>
                            </Card>
                        </Grid.Col>

                        <Grid.Col span={{ base: 12, sm: 6 }}>

                            <Stack gap="lg">

                                {/* Personal Information */}
                                <Paper p="md" radius="md" bg="gray.3">
                                    <Group gap="xs" align="center">
                                        <Icon icon={Icons.user} width={18} />
                                        <Title order={5}>Personal Information</Title>
                                    </Group>
                                </Paper>

                                <Grid gutter="md" px="sm">
                                    <Grid.Col span={{ base: 12 }}>
                                        <TextInput label="Email" value={profile.email || ""} disabled />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                        <TextInput
                                            label="First Name"
                                            name="first_name"
                                            value={values.first_name}
                                            onChange={(e) => setFieldValue("first_name", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.first_name}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 6 }}>
                                        <TextInput
                                            label="Last Name"
                                            name="last_name"
                                            value={values.last_name}
                                            onChange={(e) => setFieldValue("last_name", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.last_name}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <TextInput
                                            label="Phone"
                                            name="phone"
                                            value={formatPhone(values.phone)}
                                            onChange={(e) => setFieldValue("phone", unformatPhone(e.target.value))}
                                            onBlur={handleBlur}
                                            error={errors.phone}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <DateInput
                                            label="Birthday"
                                            name="bday"
                                            value={parseDateValue(values.bday)}
                                            onChange={(value) => {
                                                if (typeof value === "string") {
                                                    setFieldValue("bday", value)
                                                }
                                                if (value === null) {
                                                    setFieldValue("bday", "")
                                                }
                                                if (typeof value === "object" && value !== null) {
                                                    setFieldValue("bday", (value as Date).toISOString())
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            error={errors.bday}
                                            clearable
                                            dateParser={(dateString) => new Date(Date.parse(dateString))}
                                            leftSection={<Icon icon="ph:calendar-blank-duotone" width={18} />}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <NativeSelect
                                            label="Timezone"
                                            name="timezone"
                                            data={[
                                                { label: "Select Timezone...", value: "" },
                                                ...timezoneOptions
                                            ]}
                                            value={values.timezone}
                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                setFieldValue("timezone", e.target.value)
                                            }}
                                            onBlur={handleBlur}
                                            error={errors.timezone}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={12}>
                                        <TextInput
                                            label="Address Line 1"
                                            name="address_line1"
                                            value={values.address_line1}
                                            onChange={(e) => setFieldValue("address_line1", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.address_line1}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={12}>
                                        <TextInput
                                            label="Address Line 2"
                                            name="address_line2"
                                            value={values.address_line2}
                                            onChange={(e) => setFieldValue("address_line2", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.address_line2}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <TextInput
                                            label="City"
                                            name="city"
                                            value={values.city}
                                            onChange={(e) => setFieldValue("city", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.city}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <Select
                                            label="State"
                                            name="state"
                                            data={statesList}
                                            value={values.state}
                                            onChange={(value) => setFieldValue("state", value || "")}
                                            onBlur={handleBlur}
                                            error={errors.state}
                                            searchable
                                            clearable
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, sm: 4 }}>
                                        <TextInput
                                            label="Postal Code"
                                            name="postal_code"
                                            value={values.postal_code}
                                            onChange={(e) => setFieldValue("postal_code", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.postal_code}
                                        />
                                    </Grid.Col>
                                </Grid>

                            </Stack>

                        </Grid.Col>

                        <Grid.Col span={{ base: 12, sm: 6 }}>

                            <Stack gap="lg">

                                {/* DJ Settings */}
                                <Paper p="md" radius="md" bg="gray.3">
                                    <Group gap="xs" align="center">
                                        <Icon icon="hugeicons:profile-02" width={18} />
                                        <Title order={5}>DJ Settings</Title>
                                    </Group>
                                </Paper>

                                <Grid gutter="md" px="sm">
                                    <Grid.Col span={{ base: 12 }}>
                                        <TextInput
                                            label="DJ Name / Alias"
                                            name="alias"
                                            value={values.alias}
                                            onChange={(e) => setFieldValue("alias", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.alias}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12 }}>
                                        <Textarea
                                            label="Bio"
                                            name="bio"
                                            value={values.bio}
                                            onChange={(e) => setFieldValue("bio", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.bio}
                                            minRows={3}
                                            autosize
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12 }}>
                                        <MultiSelect
                                            label="Genres Played"
                                            name="genres"
                                            data={[
                                                "Pop",
                                                "Hip-Hop/Rap",
                                                "R&B/Soul",
                                                "Rock",
                                                "Country",
                                                "Latin/Reggaeton",
                                                "Electronic/Dance (EDM)",
                                                "Alternative/Indie",
                                                "Funk",
                                                "Jazz",
                                                "Blues",
                                                "Gospel/Christian",
                                                "Metal",
                                                "Punk",
                                                "Folk/Americana",
                                                "Reggae",
                                                "Afrobeats",
                                                "K-Pop",
                                                "Lo-Fi/Chillhop",
                                                "House/Techno"
                                            ]}
                                            value={values.genres && values.genres.length ? values.genres.split(",") : []}
                                            onChange={(value) => setFieldValue("genres", value.join(","))}
                                            onBlur={handleBlur}
                                            error={errors.genres}
                                            clearable
                                            searchable
                                            hidePickedOptions
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12 }}>
                                        <TextInput
                                            label="Social Links"
                                            name="website"
                                            value={values.website}
                                            onChange={(e) => setFieldValue("website", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.website}
                                            leftSection={<Icon icon="ph:globe-duotone" width={18} />}
                                            placeholder="Website URL"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12 }}>
                                        <TextInput
                                            name="instagram"
                                            value={values.instagram}
                                            onChange={(e) => setFieldValue("instagram", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.instagram}
                                            leftSection={<Icon icon="mdi:instagram" width={18} />}
                                            placeholder="Instagram URL"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12 }}>
                                        <TextInput
                                            name="facebook"
                                            value={values.facebook}
                                            onChange={(e) => setFieldValue("facebook", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.facebook}
                                            leftSection={<Icon icon="mdi:facebook" width={18} />}
                                            placeholder="Facebook URL"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12 }}>
                                        <TextInput
                                            name="x"
                                            value={values.x}
                                            onChange={(e) => setFieldValue("x", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.x}
                                            leftSection={<Icon icon="ri:twitter-x-fill" width={16} />}
                                            placeholder="X / Twitter URL"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12 }}>
                                        <TextInput
                                            name="tiktok"
                                            value={values.tiktok}
                                            onChange={(e) => setFieldValue("tiktok", e.target.value)}
                                            onBlur={handleBlur}
                                            error={errors.tiktok}
                                            leftSection={<Icon icon="ic:baseline-tiktok" width={18} />}
                                            placeholder="TikTok URL"
                                        />
                                    </Grid.Col>
                                </Grid>

                            </Stack>

                        </Grid.Col>

                        {/* Save / Cancel */}
                        <Grid.Col span={{ base: 12 }}>

                            {/* Save / Cancel */}
                            <Group justify="flex-end" pb="xl">
                                <Button
                                    variant="default"
                                    onClick={() => resetForm()}
                                    disabled={!dirty}
                                    size="lg"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="green"
                                    onClick={submitForm}
                                    loading={isSubmitting}
                                    disabled={!dirty || !isValid}
                                    size="lg"
                                >
                                    Save
                                </Button>
                            </Group>

                        </Grid.Col>

                    </Grid>
                </Box>
            )}
        </Formik>
    )
}
